import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import { Order, OrderListItem, OrderStatus, PaginatedResponse } from '@core/models';
import { TableParams, TableResponse } from '@shared/components';

/**
 * Order Service
 * Handles dispensation order management
 */
@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'intake/orders';

  /**
   * Get orders for data table
   * Supports pagination, sorting, and date/status filtering
   */
  async getOrdersForTable(
    params: TableParams,
    filterDate?: string,
    statusIn?: OrderStatus[],
  ): Promise<TableResponse<OrderListItem>> {
    const httpParams: Record<string, string> = {
      page: params.page.toString(),
      page_size: params.pageSize.toString(),
    };

    if (filterDate) {
      httpParams['created_at__date'] = filterDate;
    }

    if (statusIn?.length) {
      httpParams['status__in'] = statusIn.join(',');
    }

    if (params.sortBy) {
      const prefix = params.sortOrder === 'desc' ? '-' : '';
      httpParams['ordering'] = `${prefix}${params.sortBy}`;
    }

    const response = await firstValueFrom(
      this.http
        .get<PaginatedResponse<OrderListItem>>(`${this.baseUrl}/`, {
          params: httpParams,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );

    return {
      data: response.results,
      total: response.count,
    };
  }

  /**
   * Get order by ID with full details (includes prescriptions)
   */
  async getById(id: number): Promise<Order> {
    return await firstValueFrom(
      this.http.get<Order>(`${this.baseUrl}/${id}/`).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Create a new order grouping UPLOADS for review and (post-approval)
   * dispensing. The recipient is assigned later, in the review/approval step.
   */
  async create(data: { upload_ids: number[]; branch_id: number }): Promise<Order> {
    return await firstValueFrom(
      this.http.post<Order>(`${this.baseUrl}/`, data).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /** Assign (or replace) the order's recipient during review. */
  async setRecipient(id: number, recipientId: number): Promise<Order> {
    return await firstValueFrom(
      this.http
        .post<Order>(`${this.baseUrl}/${id}/set_recipient/`, { recipient_id: recipientId })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /**
   * Name one of the order's own patients as the receptor, or pass `null` to
   * clear the answer. The backend builds the recipient from that catalog
   * patient and derives `patient_receives` on every matching formula.
   */
  async setReceiverPatient(id: number, patientId: number | null): Promise<Order> {
    return await firstValueFrom(
      this.http
        .post<Order>(`${this.baseUrl}/${id}/set_receiver_patient/`, {
          patient_id: patientId,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /** Cancel the whole order (soft-cancels its formulas). */
  async cancel(id: number): Promise<Order> {
    return await firstValueFrom(
      this.http.post<Order>(`${this.baseUrl}/${id}/cancel/`, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /** Approve a reviewed order → it becomes dispensable (to_dispense). */
  async approve(id: number): Promise<Order> {
    return await firstValueFrom(
      this.http.post<Order>(`${this.baseUrl}/${id}/approve/`, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }
}
